import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { SplitActionButtonComponent, SplitActionMenuItem, TooltipModule } from '@libs/ui-lib';
import { routes } from '@libs/utils';
import { TranslateModule } from '@ngx-translate/core';
import { TagEvent, TagPusherService } from '@libs/shared-services';
import { Store } from '@ngrx/store';
import { patientActions } from '@libs/store';
import { HealthHistoryDialogComponent, HealthHistoryDialogData } from '@libs/home';
import { Dialog, DialogRef } from '@angular/cdk/dialog';

@Component({
  selector: 'lib-patient-item-actions',
  templateUrl: './patient-item-actions.component.html',
  imports: [SplitActionButtonComponent, TooltipModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientItemActionsComponent {
  private readonly router = inject(Router);
  private readonly tagPusherService = inject(TagPusherService);
  private readonly store = inject(Store);
  private readonly dialog = inject(Dialog);

  public patientId = input.required<string>();
  public siteId = input.required<string>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dialogRef: DialogRef<any> | null = null;

  // eslint-disable-next-line unicorn/consistent-function-scoping
  protected readonly menuItems = computed<SplitActionMenuItem[]>(() => {
    const patientId = this.patientId();
    return [
      {
        label: 'patientSearch_chart_button',
        icon: 'px-24-chart',
        action: this.onChartMenuItemClick,
      },
      {
        label: 'patientSearch_digital_button',
        icon: 'px-32-digital',
        action: [patientId, routes.imaging],
      },
      {
        label: 'patientSearch_perio_button',
        icon: 'px-32-perio',
        action: [patientId, routes.perio],
      },
      {
        label: 'patientSearch_notes_button',
        icon: 'px-24-edit-note',
        action: [patientId, routes.notes],
      },
      {
        label: 'patientSearch_collab_button',
        icon: 'px-24-collab',
        action: [patientId, routes.collab],
      },
      {
        label: 'patientSearch_patient_profile_button',
        icon: 'px-24-history',
        action: this.openHealthHistoryModal,
      },
    ];
  });

  protected readonly openHealthHistoryModal = (): void => {
    this.store.dispatch(patientActions.onOpenHealthHistory({ patientId: this.patientId() }));
    this.dialogRef = this.dialog.open<HealthHistoryDialogComponent, HealthHistoryDialogData>(HealthHistoryDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[1356px]', 'h-full', 'pt-[84px]', 'pb-[12px]'],
      data: {
        siteId: this.siteId(),
        patientId: this.patientId(),
      },
    });
  };

  protected readonly onChartMenuItemClick = (): void => {
    this.router.navigate([routes.chartingOnSiteForPatient(this.siteId(), this.patientId())]);
    this.tagPusherService.pushTag({
      event: TagEvent.ChartingOpened,
      location: 'patient_search',
      patient_id: this.patientId(),
    });
  };
}
